/* eslint no-restricted-syntax: "off" */
/* eslint no-await-in-loop: "off" */

import taskService from 'resources/task/task.service';
import milestoneService from 'resources/milestone/milestone.service';
import listedkitService from 'resources/listedkit/listedkit.service';
import companyService from 'resources/company/company.service';

import * as companyHelpers from 'resources/company/company.helpers';
import * as documentApi from 'resources/signnow/api/document.api';

import { getListedKitType } from 'resources/listedkit/listedkit.helpers';

import auditService from 'resources/audit/audit.service';
import { v4 as uuidv4 } from 'uuid';
import { getMilestonesWithTasks } from './getMilestonesWithTasks';
import { LISTEDKIT_TYPES } from '../listedkit.constants';

const createListedKit = async (listedKit, companyId) => {
  const defaultMilestones = [];

  if (listedKit.milestones) {
    const milestones = listedKit.milestones.filter((m) => !m.isAudit);

    for (const milestone of milestones) {
      const defaultMilestone = await milestoneService.createMilestone({
        companyId,
        name: milestone.name,
        type: milestone.type,
        dueInDays: milestone.due_in_days,
        dueRelativeDate: milestone.due_relative_date,
        editable: milestone.editable,
        status: milestone.status,
        isAfterAction: milestone.is_after_action,
      });

      // creating milestone tasks
      for (const [index, task] of milestone.tasks.entries()) {
        await taskService.createTask({
          milestoneId: defaultMilestone.id,
          milestoneOrder: index,
          companyId,
          content: task.content,
          assigneeType: task.assignee_type,
          dueInDays: task.due_in_days,
          isAfterAction: task.is_after_action,
          dueRelativeDate: task.due_relative_date,
          ...(task.predefined && { predefined: task.predefined }),
          ...(task.type && { type: task.type }),
          ...(task.is_optional && { isOptional: task.is_optional }),
          ...(task.template_id && { templateId: task.template_id }),
          ...(task.description && { description: task.description }),
          ...(task.is_hidden_from_client && { isHiddenFromClient: task.is_hidden_from_client }),
          ...(task.is_auto && { isAuto: task.is_auto }),
          ...(task.send_time && { sendTime: task.send_time }),
          ...(task.time_zone && { timeZone: task.time_zone }),
        });
      }

      defaultMilestones.push(defaultMilestone);
    }
  }

  const createdListedKit = await listedkitService.createListedKit({
    name: listedKit.name,
    type: listedKit.type,
    company_id: companyId,
    user_id: listedKit.userId,
    ...(defaultMilestones.length && { milestone_ids: defaultMilestones.map((item) => item.id) }),
    ...((listedKit.type === LISTEDKIT_TYPES.BUYER_GHOST || listedKit.type === LISTEDKIT_TYPES.SELLER_GHOST) && { is_ghost: true }),
  });

  if (listedKit.audits) {
    for (const audit of listedKit.audits) {
      await auditService.createAudit({
        id: uuidv4(),
        companyId,
        name: audit.name,
        type: audit.type,
        listedkitId: createdListedKit.id,
        dueInDays: audit.due_in_days,
        isAfterAction: audit.is_after_action,
        dueRelativeDate: audit.due_relative_date,
      });
    }
  }

  const company = await companyService.getCompanyById(companyId);

  const isEsignatureEnabled = companyHelpers.isEsignatureEnabled(company);

  if (isEsignatureEnabled) {
    const attachedSignDocuments = await documentApi.getAttachedSignDocuments({ listedkitId: listedKit.id });
    const attachedSignDocumentIds = attachedSignDocuments
      ? attachedSignDocuments.map((document) => document.listedKitDocument.id)
      : [];

    // copy e-signature documents to new kit
    await Promise.all(
      attachedSignDocumentIds.map((documentId) => (
        documentApi.attachToListedkit({
          documentId,
          listedkitId: createdListedKit.id,
        })
      )) || [],
    );
  }

  return createdListedKit;
};

const createDefaultKitTemplates = async (defaultKitTemplates, companyId) => (
  Promise.all(defaultKitTemplates.map(async (defaultKitTemplate) => {
    await createListedKit(defaultKitTemplate, companyId);
  })));

const prepareDataAndCreateNewListedKit = async ({
  dealType,
  dealStage,
  companyId,
  selectedListedKit,
  kitName,
  userId,
}) => {
  const milestones = await getMilestonesWithTasks(selectedListedKit);
  const audits = await auditService.getKitAudits(selectedListedKit.id);

  const newListedKit = {
    name: kitName,
    type: getListedKitType(dealType, dealStage),
    milestones,
    audits,
    userId,
    id: selectedListedKit.id,
  };

  const createdListedKit = await createListedKit(newListedKit, companyId);
  return createdListedKit;
};

export {
  createDefaultKitTemplates,
  createListedKit,
  prepareDataAndCreateNewListedKit,
};
